import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Box,
    CircularProgress,
    Switch,
    FormControlLabel,
    Divider,
    Typography
} from "@mui/material";
import React, { useState } from "react";
import axios from "axios";
import { path } from "../../../routes";

const AddDisplaymodal = (props) => {
    const [name, setName] = useState("");
    const [type, setType] = useState("");
    const [loading, setLoading] = useState(false);

    // Runtime / kiosk settings (saved per device in backend)
    const [slideIntervalSec, setSlideIntervalSec] = useState(15);
    const [objectFit, setObjectFit] = useState('contain'); // contain | cover
    // Default: vše vypnuto (Logo / Clock / Date / Temperature)
    const [showLogo, setShowLogo] = useState(false);
    const [showClock, setShowClock] = useState(false);
    const [showDate, setShowDate] = useState(false);
    const [showTemp, setShowTemp] = useState(false);
    const [demoRatio, setDemoRatio] = useState('9:16'); // 9:16 | 16:9 | 4:3

    const handleNameChange = (event) => {
        setName(event.target.value);
    };

    const handleTypeChange = (event) => {
        setType(event.target.value);
    };

    const close_handler = () => {
        props.close();
    };

    const handleSubmit = async () => {
        if (!name.trim() || !type) {
            alert("Please fill in all required fields");
            return;
        }

        setLoading(true);
        try {
            const resp = await axios.post(`${path}/addnewdisplay`, {
                name: name,
                type: type,
                organization: props.org,
            });

            const createdId = resp?.data?.id;

            // Save runtime settings (per-device) right after create
            if (createdId) {
                const settingsPayload = {
                    slideIntervalSec: Math.max(3, Math.min(300, Number(slideIntervalSec || 15))),
                    objectFit: objectFit === 'cover' ? 'cover' : 'contain',
                    showLogo: Boolean(showLogo),
                    showClock: Boolean(showClock),
                    showDate: Boolean(showDate),
                    showTemp: Boolean(showTemp),
                    demoRatio: String(demoRatio || '9:16'),
                };
                try {
                    await axios.put(`${path}/setDeviceSettings/${createdId}`, { settings: settingsPayload });
                } catch (e) {
                    const status = e?.response?.status;
                    const data = e?.response?.data;
                    if (status === 409 && data?.missingTable) {
                        alert(
                            `Display created, but runtime settings could not be saved because database table is missing: ${data.missingTable}\n\n` +
                            `${data.hint || 'Run the migration SQL on the production database.'}`
                        );
                    } else {
                        alert("Display created, but failed to save runtime settings.");
                    }
                }
            }

            alert("Display saved successfully");
            props.close();
        } catch (error) {
            console.error("Error saving display:", error);
            alert("Error saving display");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={true}
            onClose={close_handler}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: { xs: 0, sm: "12px" },
                    maxHeight: { xs: "100vh", sm: "90vh" },
                    margin: { xs: 0, sm: "32px" },
                    width: { xs: "100%", sm: "auto" },
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: { xs: "18px", sm: "20px" },
                    fontWeight: 600,
                    pb: 1.5,
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                Add Display
            </DialogTitle>

            <DialogContent
                sx={{
                    pt: 3,
                    pb: 3,
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        pt: 2,
                    }}
                >
                    <TextField
                        sx={{ width: "100%" }}
                        id="name"
                        label="Device Name"
                        variant="outlined"
                        value={name}
                        onChange={handleNameChange}
                        fullWidth
                        required
                    />
                    <FormControl sx={{ width: "100%" }}>
                        <InputLabel id="device-type-label">Device Type</InputLabel>
                        <Select
                            labelId="device-type-label"
                            value={type}
                            label="Device Type"
                            onChange={handleTypeChange}
                        >
                            <MenuItem value={1}>Display</MenuItem>
                            <MenuItem value={2}>Kiosk</MenuItem>
                        </Select>
                    </FormControl>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Runtime settings
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            (optional)
                        </Typography>
                    </Box>

                    <TextField
                        fullWidth
                        type="number"
                        label="Slide interval (s)"
                        value={slideIntervalSec}
                        inputProps={{ min: 3, max: 300, step: 1 }}
                        onChange={(e) => setSlideIntervalSec(Math.max(3, Math.min(300, Number(e.target.value || 15))))}
                    />

                    <FormControl sx={{ width: "100%" }}>
                        <InputLabel id="fit-label">Object-fit</InputLabel>
                        <Select
                            labelId="fit-label"
                            value={objectFit}
                            label="Object-fit"
                            onChange={(e) => setObjectFit(e.target.value)}
                        >
                            <MenuItem value="contain">contain (no distortion)</MenuItem>
                            <MenuItem value="cover">cover</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ width: "100%" }}>
                        <InputLabel id="ratio-label">Demo aspect ratio</InputLabel>
                        <Select
                            labelId="ratio-label"
                            value={demoRatio}
                            label="Demo aspect ratio"
                            onChange={(e) => setDemoRatio(e.target.value)}
                        >
                            <MenuItem value="9:16">9:16 (portrait)</MenuItem>
                            <MenuItem value="16:9">16:9 (landscape)</MenuItem>
                            <MenuItem value="4:3">4:3</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <FormControlLabel control={<Switch checked={showLogo} onChange={(e) => setShowLogo(e.target.checked)} />} label="Logo" />
                        <FormControlLabel control={<Switch checked={showClock} onChange={(e) => setShowClock(e.target.checked)} />} label="Clock" />
                        <FormControlLabel control={<Switch checked={showDate} onChange={(e) => setShowDate(e.target.checked)} />} label="Date" />
                        <FormControlLabel control={<Switch checked={showTemp} onChange={(e) => setShowTemp(e.target.checked)} />} label="Temperature" />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2.5 },
                    pt: 2,
                    borderTop: "1px solid #e0e0e0",
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column", sm: "row" },
                }}
            >
                <Button
                    onClick={close_handler}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: 120 },
                        height: { xs: "48px", sm: "auto" },
                        order: { xs: 2, sm: 1 },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    disabled={!name.trim() || !type || loading}
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: 120 },
                        height: { xs: "48px", sm: "auto" },
                        fontWeight: 600,
                        order: { xs: 1, sm: 2 },
                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                        "&:hover": {
                            boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                        },
                        "&:disabled": {
                            backgroundColor: "#e0e0e0",
                            color: "#9e9e9e",
                        },
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            <span>Saving...</span>
                        </Box>
                    ) : (
                        "Save"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddDisplaymodal;
